import React, { useRef, useEffect, useState } from 'react';
import ConfirmModal from './components/ConfirmModal';
import BreakSelectionModal from './components/BreakSelectionModal';
import TeamSection from './TeamSection';
import TeamFoulsCounter from './TeamFoulsCounter';
import GameTimer from './GameTimer';
import PersonalPenalties from './PersonalPenalties';
import TeamInferiority from './TeamInferiority';
import ImageCarousel from './ImageCarousel';
import Settings from './Settings';
import TimeoutOverlay from './TimeoutOverlay';
import BreakOverlay from './BreakOverlay';
import EntertainmentSettings from './EntertainmentSettings';
import InGameOverlay from './InGameOverlay';
import InGameMiniOverlay from './InGameMiniOverlay';
import MultimediaControl from './MultimediaControl';
import MusicMiniPlayer from './MusicMiniPlayer';
import { useLocation } from 'react-router-dom';
import { useScoreboard } from './context/ScoreboardContext';
import { useMusic } from './context/MusicContext';
import { useLanguage } from './context/LanguageContext';
import { useDashboardLanguage } from './context/DashboardLanguageContext';
import useGoogleAnalytics from './hooks/useGoogleAnalytics';
import { playBuzzer } from './sounds/buzzer';

const Scoreboard = () => {
  useGoogleAnalytics();
  const location = useLocation();
  const isLiveView = location.pathname === '/liveview';
  const penaltyTimersRef = useRef([]);
  const [liveViewWindow, setLiveViewWindow] = useState(null);
  const [videoPlayerWindow, setVideoPlayerWindow] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEntertainmentMenuOpen, setIsEntertainmentMenuOpen] = useState(false);
  const [timeoutRunning, setTimeoutRunning] = useState(false);
  const [showInGameOverlay, setShowInGameOverlay] = useState(false);
  const [showMultimediaControl, setShowMultimediaControl] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  // Modal state for reset confirmation
  const [showResetModal, setShowResetModal] = useState(false);
  const [pendingReset, setPendingReset] = useState(false);

  const {
    homeTeam,
    awayTeam,
    setTeamScore,
    setTeamName,
    setTeamFoulsAbsolute,
    isGameRunning,
    resetMatch,
    backgroundColor,
    defaultGameDuration,
    carouselImages,
    activeTimeout,
    // STAGE 4: timeout command helpers (store-backed) replace the legacy setters.
    requestTimeout,
    startTimeout,
    endTimeout,
    decreaseTimeoutCount,
    showTimeoutOverlay,
    setShowTimeoutOverlay,
    setShowSettings,
    showSettings,
    defaultBreakDuration,
    overtimeBreak1Duration,
    overtimeBreak2Duration,
    showBreakOverlay,
    showBreakSelectionModal,
    setShowBreakSelectionModal,
    startManualBreak,
    showEntertainmentSettings,
    setShowEntertainmentSettings,
    isEntertainmentEnabled,
    homeTimeoutCount,
    awayTimeoutCount
  } = useScoreboard();

  const { setIsMusicPlaying } = useMusic();
  const { t } = useLanguage();
  const { t: dt } = useDashboardLanguage();

  // Listen for video state updates
  useEffect(() => {
    const handleVideoStateUpdate = (event) => {
      if (event.data?.type === 'VIDEO_STATE_UPDATE') {
        setIsVideoPlaying(event.data.isPlaying);
        setCurrentVideoIndex(event.data.currentVideoIndex);
      }
    };

    window.addEventListener('message', handleVideoStateUpdate);
    return () => window.removeEventListener('message', handleVideoStateUpdate);
  }, []);

  // Keep local timeoutRunning in sync: when overlay closes (auto-expiry or cancel), reset it
  useEffect(() => {
    if (!showTimeoutOverlay) {
      setTimeoutRunning(false);
    }
  }, [showTimeoutOverlay]);
  
  // Schließe alle Fenster, wenn das Hauptfenster geschlossen wird
  useEffect(() => {
    const handleBeforeUnload = () => {
      // LiveView-Fenster schließen, falls geöffnet
      if (liveViewWindow && !liveViewWindow.closed) {
        liveViewWindow.postMessage({ type: 'CLOSE_LIVE_VIEW' }, '*');
      }
      
      // VideoPlayer-Fenster schließen, falls geöffnet
      if (videoPlayerWindow && !videoPlayerWindow.closed) {
        videoPlayerWindow.postMessage({ type: 'STOP_VIDEO' }, '*');
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [liveViewWindow, videoPlayerWindow]);

  useEffect(() => {
    if (!isGameRunning) {
      penaltyTimersRef.current.forEach(timer => {
        if (timer && timer.setIsActive) {
          timer.setIsActive(false);
        }
      });
    }
  }, [isGameRunning]);

  useEffect(() => {
    if (!isGameRunning && activeTimeout) {
      // Don't automatically show the timeout overlay when the game stops
      // setShowTimeoutOverlay(true);
    } else if (isGameRunning) {
      setShowTimeoutOverlay(false);
    }
  }, [isGameRunning, activeTimeout, setShowTimeoutOverlay]);

  useEffect(() => {
    const handleKeyPress = (event) => {
      const activeElement = document.activeElement;
      // Don't trigger if user is typing in an input field
      if (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA') {
        return;
      }

      // Horn: "h" as before, and "b" for buzzer — the desktop edition has used "b" since the first
      // live match, and an operator switching between the two should not have to remember which.
      const key = event.key.toLowerCase();
      if (key === 'h' || key === 'b') {
        event.preventDefault(); // Prevent any default key behavior
        playBuzzer();
      }
    };

    const handleToggleInGameOverlay = (event) => {
      if (event.detail?.action === 'close') {
        setShowInGameOverlay(false);
      } else {
        setShowInGameOverlay(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    window.addEventListener('toggleInGameOverlay', handleToggleInGameOverlay);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      window.removeEventListener('toggleInGameOverlay', handleToggleInGameOverlay);
    };
  }, []);

  // STAGE 2: score/fouls/name writes go through the MatchStore via context commands.
  const updateScore = (team, increment) => {
    setTeamScore(team, increment); // store clamps at 0
  };

  const updateFouls = (team, fouls) => {
    setTeamFoulsAbsolute(team, fouls); // onFoulChange passes the new absolute value
  };

  const updateTeamName = (team, name) => {
    setTeamName(team, name);
  };

  // (removed duplicate modal state definitions)

const handleReset = () => {
  setShowResetModal(true);
};

const confirmResetAction = () => {
  setShowResetModal(false);
  setPendingReset(true);
};

useEffect(() => {
  if (pendingReset) {
    // STAGE 6: store-backed reset. resetMatch() atomically clears scores/penalties/inferiority/
    // timeout/break, sets period 1 + the default clock duration (team names kept), and the
    // MatchStore's SyncTransport mirrors the whole cleared state to the live view automatically.
    // It also fires the resetAll window-event (which clears names→'' and the UI request/overlay
    // flags). This replaces the old RESET_GAME dispatch shim — the last dispatch is gone.
    resetMatch(defaultGameDuration);

    setPendingReset(false);
  }
}, [pendingReset, resetMatch, defaultGameDuration, liveViewWindow]);

  // STAGE 4: timeout request/start/end/cancel go through the store via the context command
  // helpers. requestTimeout flags the team (no countdown/count); startTimeout begins the fixed
  // 60 s countdown + increments the per-team counter; endTimeout clears everything.
  const handleTimeoutRequest = (team) => {
    // If either team has an active timeout, don't allow another timeout (store-guarded too).
    if (homeTeam.hasTimeout || awayTeam.hasTimeout) {
      return;
    }
    requestTimeout(team);
    // Don't automatically show the timeout overlay (it opens on Start).
  };

  const handleStartTimeout = (team) => {
    setTimeoutRunning(true);
    startTimeout(team);
  };

  const handleTimeoutEnd = () => {
    setTimeoutRunning(false);
    endTimeout();
  };

  const handleCancelTimeout = (team) => {
    setTimeoutRunning(false);
    // Cancel clears the running/requested timeout + both teams' hasTimeout (count untouched).
    endTimeout();
  };

  const handleDecreaseTimeout = (team) => {
    decreaseTimeoutCount(team);
  };

  const openLiveView = () => {
    if (liveViewWindow && !liveViewWindow.closed) {
      // Send postMessage to close the window
      liveViewWindow.postMessage({ type: 'CLOSE_LIVE_VIEW' }, '*');
      // Optionally clear reference after a short delay
      setTimeout(() => {
        setLiveViewWindow(null);
      }, 500);
      return;
    }

    const width = 1024;
    const height = 576;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const newWindow = window.open(
      '/liveview',
      'Live View',
      `width=${width},height=${height},left=${left},top=${top}`
    );

    setLiveViewWindow(newWindow);
  };

  const openVideoPlayer = (videoIndex = 0) => {
    if (videoPlayerWindow && !videoPlayerWindow.closed) {
      // If window exists, send message to change video
      videoPlayerWindow.postMessage({ type: 'CHANGE_VIDEO', index: videoIndex }, '*');
      videoPlayerWindow.focus();
      return;
    }

    const width = 800;
    const height = 600;
    const left = window.screen.width - width;
    const top = 0;

    const newWindow = window.open(
      `/videoplayer?index=${videoIndex}`,
      'VideoPlayer',
      `width=${width},height=${height},left=${left},top=${top}`
    );

    if (newWindow) {
      setVideoPlayerWindow(newWindow);
      window.videoPlayerWindow = newWindow;
      newWindow.focus();
      
      // Request initial video state after a short delay to ensure the window is loaded
      setTimeout(() => {
        newWindow.postMessage({ type: 'REQUEST_VIDEO_STATE' }, '*');
      }, 1000);

      newWindow.addEventListener('beforeunload', () => {
        window.videoPlayerWindow = null;
      });
    }
  };

  // Check if live view window is actually open
  const isLiveViewOpen = liveViewWindow && !liveViewWindow.closed;

  return (
    <div className="h-screen w-screen flex flex-col bg-white relative">
      {showTimeoutOverlay && (
        <TimeoutOverlay 
          team={activeTimeout?.team === 'home' ? homeTeam : awayTeam}
          timeLeft={activeTimeout?.timeLeft}
          onTimeoutEnd={handleTimeoutEnd} 
          isLiveView={isLiveView} 
        />
      )}
      {showBreakOverlay && <BreakOverlay />}
      {showSettings && (
        <Settings onClose={() => setShowSettings(false)} />
      )}
      {showEntertainmentSettings && (
        <EntertainmentSettings onClose={() => setShowEntertainmentSettings(false)} />
      )}
      {showInGameOverlay && <InGameOverlay onClose={() => setShowInGameOverlay(false)} />}
      {showMultimediaControl && <MultimediaControl 
        onClose={() => setShowMultimediaControl(false)} 
        openVideoPlayer={openVideoPlayer}
        isVideoPlaying={isVideoPlaying}
        currentVideoIndex={currentVideoIndex}
      />}

      <div className="flex-1 grid grid-cols-[1fr,auto,1fr] items-center">
        <TeamSection 
          teamName={homeTeam.name} 
          score={homeTeam.score}
          onScoreChange={(increment) => updateScore('home', increment)}
          onNameChange={(name) => updateTeamName('home', name)}
          hasTimeout={homeTeam.hasTimeout}
          onTimeoutClick={() => handleTimeoutRequest('home')}
          onStartTimeout={() => handleStartTimeout('home')}
          onCancelTimeout={() => handleCancelTimeout('home')}
          isTimeoutRequested={!isGameRunning && activeTimeout?.team === 'home'}
          isLiveView={isLiveView}
          timeoutCount={homeTimeoutCount}
          activeTimeout={activeTimeout}
          team="home"
          onDecreaseTimeout={() => handleDecreaseTimeout('home')}
          timeoutRunning={timeoutRunning}
        />
        <GameTimer />
        <TeamSection 
          teamName={awayTeam.name} 
          score={awayTeam.score}
          onScoreChange={(increment) => updateScore('away', increment)}
          onNameChange={(name) => updateTeamName('away', name)}
          hasTimeout={awayTeam.hasTimeout}
          onTimeoutClick={() => handleTimeoutRequest('away')}
          onStartTimeout={() => handleStartTimeout('away')}
          onCancelTimeout={() => handleCancelTimeout('away')}
          isTimeoutRequested={!isGameRunning && activeTimeout?.team === 'away'}
          isLiveView={isLiveView}
          timeoutCount={awayTimeoutCount}
          activeTimeout={activeTimeout}
          team="away"
          onDecreaseTimeout={() => handleDecreaseTimeout('away')}
          timeoutRunning={timeoutRunning}
        />
      </div>
      
      <div className="grid grid-cols-[1fr,auto,1fr]" style={{ backgroundColor }}>
        <div className="p-4">
          <TeamFoulsCounter 
            fouls={homeTeam.fouls} 
            onFoulChange={(fouls) => updateFouls('home', fouls)}
            isLiveView={isLiveView}
          />
        </div>
        <div className="flex flex-col items-center justify-center h-full px-[10vh] py-[2vh] mx-auto">
          <div className="text-white text-[4vh] font-bold text-center">
            {dt('teamFouls')}
          </div>
        </div>
        <div className="p-4">
          <TeamFoulsCounter 
            fouls={awayTeam.fouls}
            onFoulChange={(fouls) => updateFouls('away', fouls)}
            isLiveView={isLiveView}
          />
        </div>
      </div>

      <div className="grid grid-cols-[1fr,auto,1fr] py-[1vh]" style={{ backgroundColor }}>
        <div className="flex-1 flex flex-col items-center">
          <PersonalPenalties
            team="home"
            isLiveView={isLiveView}
          />
        </div>
        <div className="flex flex-col items-center justify-top h-full px-[10vh] mx-auto">          
          <div className="text-white text-[4vh] font-bold text-center">
            {dt('penalties')}
          </div>
          <div className="h-[10vh] flex items-center justify-top">
            <TeamInferiority isLiveView={isLiveView} />
          </div>
          <div className="bg-white p-8 w-[25vh] h-[14vh] flex items-center justify-center">
            <ImageCarousel images={carouselImages} />
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center">
          <PersonalPenalties
            team="away"
            isLiveView={isLiveView}
          />
        </div>
      </div>



      {/* Fan Menu */}
      <div className={`fixed bottom-16 left-4 z-50 ${!isMenuOpen && 'pointer-events-none'}`}>
        <div className={`bg-white p-4 rounded-lg shadow-lg flex flex-col gap-2 items-start transition-all duration-300 ${isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
          <button
            onClick={() => {
              openLiveView();
              setIsMenuOpen(false);
            }}
            className="bg-amber-500 text-black px-4 py-2 rounded-lg hover:bg-amber-600 font-bold text-base shadow-lg transform transition-transform duration-300 w-[180px]"
            style={{
              transform: isMenuOpen ? 'translateY(0)' : 'translateY(20px)',
            }}
          >
            {isLiveViewOpen ? `❌ ${t('menu.closeLiveView')}` : `📺 ${t('menu.liveView')}`}
          </button>

          <button
            onClick={() => {
              playBuzzer();
              setIsMenuOpen(false);
            }}
            className="bg-amber-500 text-black px-4 py-2 rounded-lg hover:bg-amber-600 font-bold text-base shadow-lg transform transition-transform duration-300 w-[180px]"
            style={{
              transform: isMenuOpen ? 'translateY(0)' : 'translateY(20px)',
            }}
          >
            🔊 {t('menu.buzzer')}
          </button>
          <button
            onClick={() => {
              setShowBreakSelectionModal(true);
              setIsMenuOpen(false);
            }}
            className={`px-4 py-2 rounded-lg font-bold text-base shadow-lg transform transition-transform duration-300 w-[180px] ${
              isGameRunning 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-amber-500 hover:bg-amber-600 text-black'
            }`}
            style={{
              transform: isMenuOpen ? 'translateY(0)' : 'translateY(20px)',
            }}
            disabled={isGameRunning}
            title={isGameRunning ? "Pause the game first to start a break" : "Start a break"}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="inline mr-2">
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
            {t('menu.break')}
          </button>

          <button
            onClick={() => {
              handleReset();
              setIsMenuOpen(false);
            }}
            className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 font-bold text-base shadow-lg transform transition-transform duration-300 w-[180px]"
            style={{
              transform: isMenuOpen ? 'translateY(0)' : 'translateY(20px)',
            }}
          >
            🔄 {t('menu.reset')}
          </button>

          <button
            onClick={() => {
              setShowSettings(true);
              setIsMenuOpen(false);
            }}
            className="bg-amber-500 text-black px-4 py-2 rounded-lg hover:bg-amber-600 font-bold text-base shadow-lg transform transition-transform duration-300 w-[180px]"
            style={{
              transform: isMenuOpen ? 'translateY(0)' : 'translateY(20px)',
            }}
          >
            ⚙️ {t('menu.settings')}
          </button>
        </div>
      </div>

      {/* Entertainment Menu */}
      <div className={`fixed bottom-16 right-4 z-50 ${!isEntertainmentMenuOpen && 'pointer-events-none'}`}>
        <div className={`bg-white p-4 rounded-lg shadow-lg flex flex-col gap-2 items-start transition-all duration-300 ${isEntertainmentMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
          <button
            onClick={() => {
              setIsMusicPlaying(true);
              setIsEntertainmentMenuOpen(false);
            }}
            className="bg-amber-500 text-black px-4 py-2 rounded-lg hover:bg-amber-600 font-bold text-base shadow-lg transform transition-transform duration-300 w-[180px]"
          >
            🎵 {t('entertainment.music')}
          </button>
          <button
            onClick={() => {
              setShowMultimediaControl(true);
              setIsEntertainmentMenuOpen(false);
            }}
            className="bg-amber-500 text-black px-4 py-2 rounded-lg hover:bg-amber-600 font-bold text-base shadow-lg transform transition-transform duration-300 w-[180px]"
            style={{
              transform: isEntertainmentMenuOpen ? 'translateY(0)' : 'translateY(20px)',
            }}
          >
            🎬 {t('entertainment.multimedia')}
          </button>
          <button
            onClick={() => {
              setShowInGameOverlay(true);
              setIsEntertainmentMenuOpen(false);
            }}
            className="bg-amber-500 text-black px-4 py-2 rounded-lg hover:bg-amber-600 font-bold text-base shadow-lg transform transition-transform duration-300 w-[180px]"
            style={{
              transform: isEntertainmentMenuOpen ? 'translateY(0)' : 'translateY(20px)',
            }}
          >
            🎮 {t('entertainment.inGame')}
          </button>

          <button
            onClick={() => {
              setShowEntertainmentSettings(true);
              setIsEntertainmentMenuOpen(false);
            }}
            className="bg-amber-500 text-black px-4 py-2 rounded-lg hover:bg-amber-600 font-bold text-base shadow-lg transform transition-transform duration-300 w-[180px]"
            style={{
              transform: isEntertainmentMenuOpen ? 'translateY(0)' : 'translateY(20px)',
            }}
          >
            ⚙️ {t('entertainment.settings')}
          </button>
        </div>
      </div>

      {/* Menu Toggle Button */}
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="fixed bottom-4 left-4 bg-white text-black px-6 py-3 rounded-lg hover:bg-gray-100 font-bold text-lg shadow-lg transition-all duration-300 flex items-center gap-2 w-auto justify-center pointer-events-auto z-50"
      >
        {isMenuOpen ? '❌' : '☰'} {t('menu.main')}
      </button>

      {/* Entertainment Menu Toggle Button */}
      
        {isEntertainmentEnabled && (
          <button
            onClick={() => setIsEntertainmentMenuOpen(!isEntertainmentMenuOpen)}
            className="fixed bottom-4 right-4 bg-white text-black px-6 py-3 rounded-lg hover:bg-gray-100 font-bold text-lg shadow-lg transition-all duration-300 flex items-center gap-2 w-auto justify-center pointer-events-auto z-50"
          >
            {isEntertainmentMenuOpen ? '❌' : '☰'} {t('menu.media')}
          </button>
        )}
      
      {/* Mini Music Overlay - wird nur angezeigt, wenn Musik abgespielt wird */}
      {!showMultimediaControl && !isMenuOpen && !isEntertainmentMenuOpen && <InGameMiniOverlay />}
      
      {/* Music Mini Player - shows when music is playing, stays visible even when menus are open */}
      <MusicMiniPlayer />
      


    {/* ConfirmModal for scoreboard reset */}
    <ConfirmModal
      open={showResetModal}
      title={t('confirmModal.confirmReset')}
      message={t('confirmModal.resetMessage')}
      onConfirm={confirmResetAction}
      onCancel={() => setShowResetModal(false)}
      confirmLabel={t('confirmModal.reset')}
      cancelLabel={t('confirmModal.cancel')}
    />

    {/* BreakSelectionModal for choosing break duration */}
    {showBreakSelectionModal && (
      <BreakSelectionModal
        onSelectBreak={startManualBreak}
        onCancel={() => setShowBreakSelectionModal(false)}
        defaultBreakDuration={defaultBreakDuration}
        overtimeBreak1Duration={overtimeBreak1Duration}
        overtimeBreak2Duration={overtimeBreak2Duration}
      />
    )}
  </div>
  );
};

export default Scoreboard;